"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Target, Users, Heart, Sparkles, BookOpen, Telescope } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function VertexPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        <AboutSidebar activeSection="astrology" />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-pink-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-pink-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-pink-500">/</span>
                <span className="text-sm">Vertex Analizi</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology" className="text-slate-400 hover:text-slate-200 transition-colors">
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology/advanced-systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Vertex Analizi</span>
            </div>

            <div className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 rounded-xl p-8 mb-8 border border-pink-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Target className="h-10 w-10 text-pink-400" />
                Vertex Analizi
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                "Kader noktası" olarak bilinen Vertex'in detaylı analizi. Hayatımıza giren önemli kişiler,
                kadersel karşılaşmalar ve ruhsal bağlantıların astrolojik işaretlerini inceler.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Vertex Nedir?</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5 text-pink-400" />
                      Matematiksel Tanım
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Vertex, ecliptic ile prime vertical'ın kesiştiği matematiksel nokta.
                        Anti-Vertex ile birlikte bir eksen oluşturur.
                      </p>
                      <div className="bg-pink-900/20 border border-pink-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-pink-200 mb-2">Özellikler:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• Genellikle 5., 6., 7. veya 8. evde bulunur</div>
                          <div>• Anti-Vertex karşı evde yer alır</div>
                          <div>• Enlem 0°-66° arasında hesaplanabilir</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-rose-400" />
                      Sembolik Anlamı
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        "Kadersel karşılaşmalar noktası" - kontrol edemediğimiz
                        ancak hayatımızı derinden etkileyen olaylar.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Önemli kişilerle karşılaşma</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Karma bağlantılar</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Ruhsal gelişim fırsatları</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Evlere Göre Vertex</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-blue-300">5. ve 6. Ev Vertex</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">5. Ev Vertex</div>
                      <div className="text-sm text-slate-400">Yaratıcı ifade, romantik aşk, çocuklar yoluyla kadersel bağlar</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">6. Ev Vertex</div>
                      <div className="text-sm text-slate-400">İş hayatı, sağlık, günlük rutin ve hizmet yoluyla karşılaşmalar</div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-green-300">7. ve 8. Ev Vertex</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">7. Ev Vertex</div>
                      <div className="text-sm text-slate-400">Ortaklıklar, evlilik, önemli ilişkiler yoluyla kadersel buluşmalar</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">8. Ev Vertex</div>
                      <div className="text-sm text-slate-400">Dönüşüm, kriz, paylaşılan kaynaklar yoluyla derin bağlantılar</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Vertex Aktivasyonları</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-cyan-400" />
                      Transit Aktivasyonları
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Transit gezegenlerin Vertex'e aspect yapması önemli
                        karşılaşmalara ve yaşam değişikliklerine işaret eder.
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
                          <div className="font-medium text-cyan-200 mb-1">Conjunction (0°)</div>
                          <div className="text-slate-300">En güçlü aktivasyon - major yaşam değişikleri</div>
                        </div>
                        <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
                          <div className="font-medium text-cyan-200 mb-1">Opposition (180°)</div>
                          <div className="text-slate-300">Anti-Vertex aktivasyonu - iç farkındalık</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Heart className="h-5 w-5 text-pink-400" />
                      Synastry'de Vertex
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        İki kişinin haritasında Vertex bağlantıları güçlü
                        kadersel ilişkileri gösterir.
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="bg-pink-900/20 border border-pink-700/30 rounded-lg p-3">
                          <div className="font-medium text-pink-200 mb-1">Gezegen-Vertex Conjunction</div>
                          <div className="text-slate-300">Güçlü çekim, önemli roller</div>
                        </div>
                        <div className="bg-pink-900/20 border border-pink-700/30 rounded-lg p-3">
                          <div className="font-medium text-pink-200 mb-1">Vertex-Vertex Bağlantıları</div>
                          <div className="text-slate-300">Mutual kadersel bağ</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Kullanım</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3 text-purple-300">İlişki Analizi</h3>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• Kadersel partner belirleme</div>
                    <div>• Synastry'de özel bağlar</div>
                    <div>• Composite chart analizi</div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3 text-teal-300">Timing Analizi</h3>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• Transit zamanlaması</div>
                    <div>• Önemli karşılaşmalar</div>
                    <div>• Yaşam dönüm noktaları</div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3 text-amber-300">Ruhsal Gelişim</h3>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• Karma dersler</div>
                    <div>• Ruhsal rehberlik</div>
                    <div>• Bilinç genişlemesi</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Vertex" - Donna Henson</div>
                    <div>• "Karmic Relationships" - Martin Schulman</div>
                    <div>• "The Astrology of Fate" - Liz Greene</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">İleri Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Composite Charts" - Robert Hand</div>
                    <div>• "Synastry" - Ronald Davison</div>
                    <div>• "Relationship Astrology" - Michael Lutin</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Vertex analizi spekülatif bir tekniktir ve kadersel ilişkiler konusunda mutlak kesinlik sağlamaz.
                Bu bilgiler sadece eğitim ve farkındalık amaçlıdır, bilimsel geçerlilik iddiası taşımaz.
                İlişki kararlarında profesyonel danışmanlık alınmalıdır.
              </p>
            </div>

            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/heliocentric"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Heliocentric Astroloji
              </Link>

              <Link
                href="/about/astrology/advanced-systems/solar-arc"
                className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors"
              >
                Solar Arc Directions
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}